/// <reference types="@types/applepayjs" />
import { useCallback, useEffect, useState } from 'react';

import { usePaymentContext } from 'features/payment';
import { IApplePayDetails } from 'features/payment/types';

import { useApplePayValidationLazyQuery } from 'generated/graphql-gateway';
import { useConfigValue } from 'hooks/configs/use-config-value';
import { useAuthContext } from 'state/auth';
import { useStoreContext } from 'state/store';
import { fullBrandName } from 'utils/environment';
import { sanitizeAlphanumeric } from 'utils/form';
import { ISOs, getCountryAndCurrencyCodes } from 'utils/form/constants';
import logger from 'utils/logger';
import { buildFormattedBillingAddress } from 'utils/native-actions';

import { useCart } from '../../../hooks/use-cart';

import { applePayCardDetails } from './apple-pay-payment-details';
import { IUseApplePay } from './types';
import useFormatApplePayCartPayload from './use-format-apple-pay-cart-payload';

declare global {
  interface Window {
    ApplePaySession: ApplePaySession | undefined;
  }
}

export default function useApplePay({
  prepaid = false,
  serverOrder,
  tipAmount,
  handleApplePay,
}: IUseApplePay) {
  const [requestingPayment, setRequestingPayment] = useState(false);
  const { isRestaurantAvailable, store, isStoreOpenAndAvailable } = useStoreContext();

  const { cartEntries } = useCart();
  const { user } = useAuthContext();
  const billingCountry = (user?.details?.isoCountryCode as ISOs) || ISOs.USA;
  const appleConfig = useConfigValue({ key: 'apple', defaultValue: {}, isRegionalized: false });
  const { countryCode, currencyCode } = getCountryAndCurrencyCodes(billingCountry);
  const { canUseApplePay } = usePaymentContext();
  const payeeName = fullBrandName();
  const merchantId = appleConfig.merchantId;
  const prepaidMerchantId = appleConfig.prepaidMerchantId;
  const { formatApplePayCartPayload } = useFormatApplePayCartPayload({
    serverOrder,
    cartEntries,
    tipAmount,
    billingCountry,
  });
  const paymentsNetworks = useConfigValue({
    key: 'apple',
    defaultValue: {},
    isRegionalized: false,
  }).paymentsNetworks;
  const [queryApplePayValidation] = useApplePayValidationLazyQuery();

  const [scriptHasMounted, setScriptHasMounted] = useState(false);

  useEffect(() => {
    if (canUseApplePay && !scriptHasMounted) {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://applepay.cdn-apple.com/jsapi/v1/apple-pay-sdk.js';
      document.body.appendChild(script);
      setScriptHasMounted(true);
    }
  }, [canUseApplePay, scriptHasMounted]);

  const onError = useCallback(
    (error: string) => {
      setRequestingPayment(false);
      logger.error({
        message: 'Device Apple Payment Failed',
        error,
      });
      handleApplePay?.({ error });
    },
    [handleApplePay]
  );
  const requestApplePayPayment = useCallback(
    ({
      total,
      cartLabels,
      cartPrices,
      paymentProcessor,
    }: {
      total: number;
      cartLabels?: string[];
      cartPrices?: number[];
      paymentProcessor?: string | null | undefined;
    }) => {
      if (!canUseApplePay || requestingPayment) {
        return;
      }
      setRequestingPayment(true);
      let lineItems;
      if (cartLabels && cartPrices && cartLabels?.length > 0 && cartPrices?.length > 0) {
        lineItems = cartLabels?.map((label, index) => ({
          label,
          amount: (cartPrices?.[index] || 0).toString(),
        }));
      } else {
        lineItems = [
          {
            label: payeeName,
            amount: total.toString(),
          },
        ];
      }
      const paymentRequestData: ApplePayJS.ApplePayPaymentRequest = {
        countryCode,
        currencyCode,
        supportedNetworks: paymentsNetworks,
        requiredBillingContactFields: ['postalAddress'],
        merchantCapabilities: ['supports3DS'],
        total: { label: payeeName, amount: (total / 100).toString() },
        lineItems,
      };
      const session = new ApplePaySession(1, paymentRequestData);
      session.oncancel = () => {
        setRequestingPayment(false);
      };
      session.onvalidatemerchant = async (event: { validationURL: any }) => {
        let merchantSession;
        try {
          merchantSession = await queryApplePayValidation({
            variables: {
              input: {
                displayName: payeeName,
                merchantIdentifier: merchantId,
                validationUrl: event.validationURL,
                initiative: 'web',
                initiativeContext: window.location.host,
              },
            },
          });
          if (!merchantSession?.data?.validateApplePay) {
            throw new Error();
          }
          session.completeMerchantValidation(merchantSession?.data?.validateApplePay);
        } catch (error) {
          logger.error('Error validating apple merchant');
          session.abort();
          setRequestingPayment(false);
        }
      };
      session.onpaymentauthorized = async event => {
        const isRestaurantAvailableForOrdering = await isRestaurantAvailable(store);
        if (!isRestaurantAvailableForOrdering && !isStoreOpenAndAvailable) {
          session.completePayment(ApplePaySession.STATUS_FAILURE);
          return onError('Store is not available');
        }
        const {
          billingContact,
          token: { paymentData, paymentMethod },
        } = event.payment;
        if (!billingContact || !paymentData) {
          session.completePayment(ApplePaySession.STATUS_FAILURE);
          return onError('Missing billing contact/payment data');
        }
        const {
          signature,
          version,
          data,
          header: { ephemeralPublicKey, publicKeyHash, transactionId },
        } = paymentData;
        const applicationData = '';
        const {
          locality: city,
          addressLines: street = [],
          country,
          postalCode,
          administrativeArea: state,
        } = billingContact;
        const formattedBillingAddress = buildFormattedBillingAddress({
          street: String(street[0]),
          city: String(city),
          state: String(state),
          postalCode: String(postalCode),
          country: String(country),
        });

        const billingAddress = {
          locality: city,
          postalCode: sanitizeAlphanumeric(postalCode),
          region: state,
          streetAddress: street[0],
        };

        const decoratedPaymentData: IApplePayDetails = {
          signature,
          applicationData,
          country: countryCode,
          data,
          decryptAlias: prepaid ? prepaidMerchantId : merchantId,
          ephemeralPublicKey,
          formatted: formattedBillingAddress,
          paymentData: JSON.stringify(paymentData),
          primary: true,
          publicKeyHash,
          transactionId,
          type: 'home',
          version,
          billingAddress,
          PaymentMethodData: {
            paymentType: paymentMethod.network,
            displayName: paymentMethod.displayName,
          },
        };

        handleApplePay?.({ data: decoratedPaymentData });
        // You can see a sample `payment` object in an image below.
        // Use the token returned in `payment` object to create the charge on your payment gateway.
        const chargeCreationSucceeds = true;
        if (chargeCreationSucceeds) {
          // Capture payment from Apple Pay
          session.completePayment(ApplePaySession.STATUS_SUCCESS);
        } else {
          // Release payment from Apple Pay
          session.completePayment(ApplePaySession.STATUS_FAILURE);
          return onError('Error charging user with apple pay');
        }
        setRequestingPayment(false);
      };
      session.begin();
    },
    [
      canUseApplePay,
      countryCode,
      currencyCode,
      handleApplePay,
      isRestaurantAvailable,
      isStoreOpenAndAvailable,
      merchantId,
      onError,
      payeeName,
      paymentsNetworks,
      prepaid,
      prepaidMerchantId,
      queryApplePayValidation,
      requestingPayment,
      store,
    ]
  );

  return {
    requestingPayment,
    requestApplePayPayment,
    applePayCardDetails,
    formatApplePayCartPayload,
  };
}
